import { useState } from "react";
import { ArrowRight, Check, Clock, Mail, Video } from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { sendForm } from "../lib/forms";
import { BOOKING_SERVICES, IMAGES } from "../data/content";

export default function Contact() {
  const [booking, setBooking] = useState({
    name: "",
    email: "",
    phone: "",
    service: BOOKING_SERVICES[1].label,
    preferred_date: "",
    notes: "",
  });
  const [bSent, setBSent] = useState(false);
  const [inquiry, setInquiry] = useState({ name: "", email: "", message: "" });
  const [iSent, setISent] = useState(false);

  const sendBooking = async (e) => {
    e.preventDefault();
    try {
      await sendForm("Session booking request", booking);
      setBSent(true);
      toast.success("Intake received — we'll confirm your slot by email.");
    } catch {
      toast.error("Couldn't send right now. Please email connectbackofthemind@gmail.com.");
    }
  };

  const sendInquiry = async (e) => {
    e.preventDefault();
    try {
      await sendForm("Contact message", { ...inquiry, topic: "General" });
      setISent(true);
      toast.success("Message received. We reply within one working day.");
    } catch {
      toast.error("Couldn't send right now. Please email connectbackofthemind@gmail.com.");
    }
  };

  return (
    <div>
      <PageHero
        eyebrow="Contact & Booking"
        lines={["Say hello.", "We answer", "kindly, and fast."]}
        sub="Book a session, send a question, or just tell us what's been sitting in the back of your mind. No receptionists, no forms with nineteen fields."
        image={IMAGES.therapyCouch}
        alt="A calm therapy space"
      />

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <Reveal>
            <div className="card-soft p-8 sm:p-10" data-testid="contact-intake-card">
              <p className="eyebrow">Intake form</p>
              <h2 className="mt-3 font-serif text-3xl text-ink">Book your session</h2>
              {bSent ? (
                <div data-testid="contact-booking-success" className="py-8 text-center">
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-sagelight text-pine">
                    <Check className="h-6 w-6" />
                  </span>
                  <p className="mt-5 font-serif text-2xl text-ink">Request received.</p>
                  <p className="mt-3 text-sm text-bodytext">
                    We'll confirm your session at <strong>{booking.email}</strong> within
                    one working day.
                  </p>
                </div>
              ) : (
                <form onSubmit={sendBooking} className="mt-6 grid gap-4 sm:grid-cols-2">
                  <input
                    data-testid="contact-name-input"
                    required
                    className="input-soft sm:col-span-1"
                    placeholder="Your name"
                    value={booking.name}
                    onChange={(e) => setBooking({ ...booking, name: e.target.value })}
                  />
                  <input
                    data-testid="contact-email-input"
                    required
                    type="email"
                    className="input-soft sm:col-span-1"
                    placeholder="Email"
                    value={booking.email}
                    onChange={(e) => setBooking({ ...booking, email: e.target.value })}
                  />
                  <input
                    data-testid="contact-phone-input"
                    className="input-soft"
                    placeholder="Phone (optional)"
                    value={booking.phone}
                    onChange={(e) => setBooking({ ...booking, phone: e.target.value })}
                  />
                  <select
                    data-testid="contact-service-select"
                    className="input-soft"
                    aria-label="Session type"
                    value={booking.service}
                    onChange={(e) => setBooking({ ...booking, service: e.target.value })}
                  >
                    {BOOKING_SERVICES.map((s) => (
                      <option key={s.id} value={s.label}>
                        {s.label} · {s.meta}
                      </option>
                    ))}
                  </select>
                  <input
                    data-testid="contact-date-input"
                    className="input-soft sm:col-span-2"
                    placeholder="Preferred date (e.g. any weekday afternoon)"
                    value={booking.preferred_date}
                    onChange={(e) => setBooking({ ...booking, preferred_date: e.target.value })}
                  />
                  <textarea
                    data-testid="contact-notes-input"
                    className="input-soft min-h-28 sm:col-span-2"
                    placeholder="What brings you here? Share as much or as little as you like."
                    value={booking.notes}
                    onChange={(e) => setBooking({ ...booking, notes: e.target.value })}
                  />
                  <button
                    data-testid="contact-submit-button"
                    className="flex items-center justify-center gap-2 rounded-full bg-pine py-3.5 text-sm font-semibold text-cream transition-all hover:-translate-y-0.5 hover:bg-pinedeep sm:col-span-2"
                  >
                    Request my session <ArrowRight className="h-4 w-4" />
                  </button>
                </form>
              )}
            </div>
          </Reveal>

          <div className="space-y-6">
            <Reveal delay={0.08}>
              <div className="rounded-[2rem] bg-pinedeep p-8 text-cream" data-testid="contact-info-card">
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-blush">
                  Practical things
                </p>
                <ul className="mt-6 space-y-5 text-sm">
                  <li className="flex gap-3.5">
                    <Video className="mt-0.5 h-4 w-4 shrink-0 text-blush" />
                    <span>
                      <strong>Fully remote practice.</strong> Sessions over a secure video
                      link — your sofa, our room, wherever feels safest.
                    </span>
                  </li>
                  <li className="flex gap-3.5">
                    <Clock className="mt-0.5 h-4 w-4 shrink-0 text-blush" />
                    <span>
                      <strong>Hours:</strong> Mon–Sat, flexible daytime and evening slots
                      across time zones.
                    </span>
                  </li>
                  <li className="flex gap-3.5">
                    <Mail className="mt-0.5 h-4 w-4 shrink-0 text-blush" />
                    <span>
                      <strong>Reply time:</strong> within one working day — usually much
                      faster.
                    </span>
                  </li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.16}>
              <div className="card-soft p-8" data-testid="contact-message-card">
                <p className="eyebrow">Prefer to just write?</p>
                {iSent ? (
                  <p data-testid="contact-inquiry-success" className="mt-4 text-sm leading-relaxed text-bodytext">
                    <strong>Thank you.</strong> Your message is with us — expect a warm
                    reply within one working day.
                  </p>
                ) : (
                  <form onSubmit={sendInquiry} className="mt-4 space-y-3">
                    <input
                      data-testid="contact-inquiry-name-input"
                      required
                      className="input-soft"
                      placeholder="Your name"
                      value={inquiry.name}
                      onChange={(e) => setInquiry({ ...inquiry, name: e.target.value })}
                    />
                    <input
                      data-testid="contact-inquiry-email-input"
                      required
                      type="email"
                      className="input-soft"
                      placeholder="Email"
                      value={inquiry.email}
                      onChange={(e) => setInquiry({ ...inquiry, email: e.target.value })}
                    />
                    <textarea
                      data-testid="contact-inquiry-message-input"
                      required
                      className="input-soft min-h-24"
                      placeholder="Your message…"
                      value={inquiry.message}
                      onChange={(e) => setInquiry({ ...inquiry, message: e.target.value })}
                    />
                    <button
                      data-testid="contact-inquiry-submit-button"
                      className="w-full rounded-full border border-pine/25 py-3 text-sm font-semibold text-pine transition-all hover:bg-pine hover:text-cream"
                    >
                      Send message
                    </button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
